import { Check, Error as ErrorIcon } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  Grid2,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { DurationTestStatus } from '@/api/definition'
import { setDurationTestStatus, getDeviceDetails } from '@/api/Device'
import { samApiClient } from '@/api/index'
import Card from '@/components/Card/Card'
import CardBody from '@/components/Card/CardBody'
import CardHeader from '@/components/Card/CardHeader'
import ConfirmDialog from '@/components/ConfirmDialogBox'
import { useAuthContext } from '@/hooks/useAuthContext'
import Indicators from '@/pages/Device/Components/Indicators'
import type { Device } from '@/pages/Device/Device'
import type { DurationTestReport } from '@/pages/Device/Reports/ReportDefinitions'
import { labelSx } from '@/styles/MuiComponentStyles'
import { layoutStyles } from '@/utils/theme'

export default function DurationTestSummaryPage(): JSX.Element {
  const { t } = useTranslation(['app', 'common'])
  const { uid } = useParams<{ uid: string }>()
  const { profile } = useAuthContext()

  const [report, setReport] = useState<DurationTestReport | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [remarks, setRemarks] = useState<string>('')
  const [finishing, setFinishing] = useState<boolean>(false)
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false)
  const [device, setDevice] = useState<Device | null>(null)

  const loadLatestReport = useCallback(async () => {
    if (!uid) return
    try {
      setIsLoading(true)
      setError(null)

      // Use v1-compatible endpoint to fetch a single device's duration test report
      const resp = await samApiClient.get<DurationTestReport>(
        `device/${uid}/duration-test-report`,
      )
      setReport(resp.data ?? null)
      setRemarks(resp.data?.remarks ?? '')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load report')
    } finally {
      setIsLoading(false)
    }
  }, [uid])

  useEffect(() => {
    void loadLatestReport()
  }, [loadLatestReport])

  useEffect(() => {
    const loadDevice = async () => {
      if (!uid) return
      try {
        const d = await getDeviceDetails(uid)
        setDevice(d)
      } catch {
        // ignore
      }
    }
    void loadDevice()
  }, [uid])

  const onFinish = async () => {
    if (!uid) return
    setFinishing(true)
    try {
      // Set status Completed with remarks (v1 parity)
      await setDurationTestStatus(
        [uid],
        DurationTestStatus.DurationTestCompleted,
        remarks,
      )
      enqueueSnackbar(
        t('device.durationTest.finishedTestSuccessfully', {
          defaultValue: 'Finished test successfully',
        }),
        { variant: 'success' },
      )
      await loadLatestReport()
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to finish duration test',
      )
      enqueueSnackbar(
        t('common:general.error', {
          defaultValue: 'Something went wrong',
        }),
        { variant: 'error' },
      )
    } finally {
      setFinishing(false)
    }
  }

  const onDiscard = async () => {
    if (!uid) return
    setFinishing(true)
    try {
      await setDurationTestStatus(
        [uid],
        DurationTestStatus.DurationTestCanceled,
        remarks,
      )
      enqueueSnackbar(
        t('device.durationTest.discardedTestSuccessfully', {
          defaultValue: 'Discarded test successfully',
        }),
        { variant: 'success' },
      )
      await loadLatestReport()
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Failed to discard duration test',
      )
      enqueueSnackbar(
        t('common:general.error', {
          defaultValue: 'Something went wrong',
        }),
        { variant: 'error' },
      )
    } finally {
      setFinishing(false)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Typography>{t('device.durationTest.durationTestSummary')}</Typography>
        <Typography sx={{ mt: 2 }}>{t('common:general.loading')}</Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  if (!report) {
    return (
      <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
        <Typography>{t('device.durationTest.durationTestSummary')}</Typography>
        <Typography sx={{ mt: 2 }}>No report found.</Typography>
      </Box>
    )
  }

  // Extract test validation logic to reduce cognitive complexity
  const getTestValidationResults = (report: DurationTestReport) => {
    const MINIMUM_GPS_MESSAGE_COUNT = 6
    const MINIMUM_HEARTBEAT_MESSAGE_COUNT = 480
    const MAXIMUM_DISCONNECTS_ALLOWED = 2

    const gpsPass = (report.noOfGPSMessages ?? 0) >= MINIMUM_GPS_MESSAGE_COUNT
    const hbPass =
      (report.noOfHeartbeats ?? 0) >= MINIMUM_HEARTBEAT_MESSAGE_COUNT
    const discPass =
      (report.noOfDisconnects ?? 0) <= MAXIMUM_DISCONNECTS_ALLOWED
    const errorCodes: number[] = (report.concerns ?? [])
      .map((c: { error_code?: number; code?: number }) =>
        typeof c?.error_code === 'number'
          ? c.error_code
          : Number(c?.error_code ?? c?.code),
      )
      .filter((n: unknown) => !Number.isNaN(Number(n)))
    const passed = gpsPass && hbPass && discPass && errorCodes.length === 0
    const verifiedState =
      report.status !== DurationTestStatus.DurationTestVerify

    return { gpsPass, hbPass, discPass, errorCodes, passed, verifiedState }
  }

  const { gpsPass, hbPass, discPass, errorCodes, passed, verifiedState } =
    getTestValidationResults(report)

  // Extract button rendering logic to reduce cognitive complexity
  const renderActionButtons = () => {
    const hasConcerns = (report.concerns ?? []).length > 0
    const isProductionOnly = profile?.productionOnly
    const finishDisabled =
      finishing || verifiedState || (!passed && !!isProductionOnly)
    const discardDisabled = finishing || passed || verifiedState

    return (
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={discardDisabled}
          sx={{
            textTransform: 'uppercase',
            backgroundColor: '#9c27b0',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': { backgroundColor: '#8e24aa', boxShadow: 'none' },
            '&.Mui-disabled': {
              backgroundColor: '#9c27b0',
              color: '#fff',
              opacity: 0.65,
            },
          }}
          type="button"
          onClick={() => void onDiscard()}
        >
          {t('device.durationTest.discardDurationTest')}
        </Button>
        <Tooltip
          title={
            hasConcerns && isProductionOnly
              ? t('device.rights.noPermissionError')
              : t('device.durationTest.finishDurationTest')
          }
        >
          <span>
            <Button
              variant="contained"
              color="success"
              disabled={finishDisabled}
              type="button"
              onClick={() => {
                if (!passed) {
                  setConfirmOpen(true)
                  return
                }
                void onFinish()
              }}
            >
              {t('device.durationTest.finishDurationTest')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    )
  }

  return (
    <Box sx={{ ...layoutStyles.pageContainer, mt: 0, p: 3 }}>
      {device && (
        <Card>
          <CardHeader>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                color: '#FFF',
                px: 1,
              }}
            >
              <Typography variant="h6">{t('common:general.info')}</Typography>
            </Box>
          </CardHeader>
          <Indicators device={device} />

          <CardBody>
            <Grid2
              container
              spacing={0}
              wrap="nowrap"
              alignItems="flex-end"
              sx={{ py: 0 }}
            >
              <Grid2>
                <Box sx={{ mr: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1 }}
                  >
                    {t('di:device.type')}
                  </Typography>
                  <Typography variant="body2">{device?.type ?? '-'}</Typography>
                </Box>
              </Grid2>
              <Grid2>
                <Box
                  sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                >
                  <Box
                    sx={{ width: '1px', bgcolor: 'divider', height: 24, mx: 1 }}
                  />
                </Box>
              </Grid2>
              {device?.version !== null && (
                <Grid2>
                  <Box sx={{ mr: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1 }}
                    >
                      {t('di:device.version')}
                    </Typography>
                    <Typography variant="body2">
                      {String(device?.version)}
                    </Typography>
                  </Box>
                </Grid2>
              )}
              {device?.version !== null && (
                <Grid2>
                  <Box
                    sx={{
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        width: '1px',
                        bgcolor: 'divider',
                        height: 24,
                        mx: 1,
                      }}
                    />
                  </Box>
                </Grid2>
              )}
              <Grid2>
                <Box sx={{ mr: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1 }}
                  >
                    {t('di:device.country')}
                  </Typography>
                  <Typography variant="body2">
                    {device?.country ?? '-'}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2>
                <Box
                  sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                >
                  <Box
                    sx={{ width: '1px', bgcolor: 'divider', height: 24, mx: 1 }}
                  />
                </Box>
              </Grid2>
              <Grid2>
                <Box sx={{ mr: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ lineHeight: 1 }}
                  >
                    {t('di:device.ownerLabel')}
                  </Typography>
                  <Typography variant="body2">
                    {device?.label ?? '-'}
                  </Typography>
                </Box>
              </Grid2>
              <Grid2>
                <Box
                  sx={{ height: '100%', display: 'flex', alignItems: 'center' }}
                >
                  <Box
                    sx={{ width: '1px', bgcolor: 'divider', height: 24, mx: 1 }}
                  />
                </Box>
              </Grid2>
              <Grid2>
                <Box sx={{ mr: 2, minWidth: 220 }}>
                  <TextField
                    variant="standard"
                    size="small"
                    label={t('di:device.serialNr')}
                    slotProps={{
                      inputLabel: { sx: labelSx, shrink: true },
                      input: { disableUnderline: true },
                    }}
                    fullWidth
                    value={device?.serial ?? '-'}
                    disabled
                  />
                </Box>
              </Grid2>
            </Grid2>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <Box sx={{ width: '100%', color: '#FFF' }}>
            <Typography variant="h4">
              {t('device.durationTest.durationTestSummary')}
            </Typography>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2">
                {report.startedAt
                  ? `${t('device.report.startedAt')}: ${new Date(
                      report.startedAt,
                    ).toLocaleString()}`
                  : ''}
              </Typography>
              <Typography variant="body2">
                {report.updatedAt
                  ? `${t('device.report.endedAt')}: ${new Date(
                      report.updatedAt,
                    ).toLocaleString()}`
                  : ''}
              </Typography>
            </Box>
          </Box>
        </CardHeader>
        <CardBody>
          <Grid2 container spacing={2}>
            <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
              <List sx={{ pl: '55px' }}>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {gpsPass ? (
                      <Check sx={{ color: 'success.main' }} />
                    ) : (
                      <ErrorIcon sx={{ color: 'warning.main' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={report.noOfGPSMessages?.toString?.() ?? '0'}
                    secondary={t('device.durationTest.gpsCount')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {hbPass ? (
                      <Check sx={{ color: 'success.main' }} />
                    ) : (
                      <ErrorIcon sx={{ color: 'warning.main' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={report.noOfHeartbeats?.toString?.() ?? '0'}
                    secondary={t('device.durationTest.heartBeatCount')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {discPass ? (
                      <Check sx={{ color: 'success.main' }} />
                    ) : (
                      <ErrorIcon sx={{ color: 'warning.main' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={report.noOfDisconnects?.toString?.() ?? '0'}
                    secondary={t('device.durationTest.disconnectCount')}
                  />
                </ListItem>
              </List>
              <List sx={{ pl: '55px' }}>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.minimumRSSIValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.minimumRSSIValue')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.maximumRSSIValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.maximumRSSIValue')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.minimumRSRPValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.minimumRSRPValue')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.maximumRSRPValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.maximumRSRPValue')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.minimumAvgRSRQValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.minimumAvgRSRQValue')}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }} />
                  <ListItemText
                    primary={report.maximumAvgRSRQValue?.toString?.() ?? ''}
                    secondary={t('device.durationTest.maximumAvgRSRQValue')}
                  />
                </ListItem>
              </List>
              <TextField
                sx={{ mt: 2 }}
                fullWidth
                id="remarks"
                label={t('common:general.remarks')}
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                required={(report.concerns ?? []).length > 0}
                error={
                  (report.concerns ?? []).length > 0 && remarks.trim() === ''
                }
                helperText={
                  (report.concerns ?? []).length > 0 && remarks.trim() === ''
                    ? t('device.durationTest.remarksRequired')
                    : ' '
                }
                variant="standard"
                size="small"
                slotProps={{
                  inputLabel: { sx: labelSx, shrink: true },
                }}
                multiline={false}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, sm: 12, md: 6 }}>
              <List>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {errorCodes.length > 0 ? (
                      <ErrorIcon sx={{ color: 'warning.main' }} />
                    ) : (
                      <Check sx={{ color: 'success.main' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      errorCodes.length > 0
                        ? t('device.calibration.testStep.hasErrors')
                        : t('device.calibration.testStep.noErrors')
                    }
                  />
                </ListItem>
                {errorCodes.map((code, idx) => {
                  const errorMessage = t(`di:rpc.error.${code}`, {
                    defaultValue: t('di:rpc.error.undefined'),
                  })
                  return (
                    <ListItem key={`${code}-${idx}`} divider>
                      <ListItemText primary={`[${code}]: ${errorMessage}`} />
                    </ListItem>
                  )
                })}
              </List>
            </Grid2>
          </Grid2>

          {renderActionButtons()}
        </CardBody>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={() => void onFinish()}
        title={t('device.durationTest.finishDurationTest')}
        confirmText={t('common:general.yes')}
        cancelText={t('common:general.cancel')}
      >
        {t(
          'device.durationTest.confirmFinish',
          'Are you sure you want to finish the duration test without all criteria passed?',
        )}
      </ConfirmDialog>
    </Box>
  )
}
